import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import {
  getClientStrategy,
  upsertClientStrategy,
  type UpsertClientStrategyParams,
} from "api/client-strategy";

import { clientStrategyKeys } from "hooks/api/query-keys";

import type {
  ClientStrategyCategoryDTO,
  ClientStrategyCategoryDTOStrategyCategory,
} from "generated/schemas";

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

export const useClientStrategy = <TData = ClientStrategyCategoryDTO>(
  clientId?: string,
  strategyCategory?: ClientStrategyCategoryDTOStrategyCategory,
  options?: UseQueryHookOptions<ClientStrategyCategoryDTO, TData>,
) =>
  useQuery({
    queryKey: clientStrategyKeys.detail(clientId ?? "", strategyCategory ?? ""),
    queryFn: () => {
      if (!clientId || !strategyCategory) {
        throw new Error("Client id and strategy category required for useClientStrategy queryFn");
      }

      return getClientStrategy({ clientId, strategyCategory });
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(strategyCategory) && (options?.enabled ?? true),
  });

export const useUpsertClientStrategy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpsertClientStrategyParams) => upsertClientStrategy(params),
    onSuccess: (strategy, { clientId, strategyCategory }) => {
      queryClient.setQueryData(
        clientStrategyKeys.detail(clientId, strategyCategory),
        strategy,
      );
    },
  });
};
